import { axios } from "utilities/axios";
import { AxiosError } from "axios";
import { compareAsc } from "date-fns";
import { useQuery, UseQueryOptions } from "react-query";
import { useTimeZone } from "hooks-for/time-zones/use-time-zone";
import {
    IInteractionMetricsSummary,
    IInteractionMetricsSummariesGetResponse,
    IInteractionsGetQueryParameters,
    Route,
    TimePeriodGroupBy,
} from "@edgetier/watchtower-types";

type IGroupType<Parameters extends IInteractionsGetQueryParameters> = Parameters["groupBy"] extends undefined
    ? null
    : Parameters["groupBy"] extends TimePeriodGroupBy
    ? Date
    : string;

/**
 * Count the number of interactions matching some conditions.
 * @param parameters Route filters.
 * @param url URL to get counts.
 */
const getInteractionMetricsSummaries = async <Parameters extends IInteractionsGetQueryParameters>(
    url: string,
    parameters: Parameters
) => {
    const response = await axios.get<IInteractionMetricsSummariesGetResponse<IGroupType<Parameters>>>(url, {
        params: { ...parameters },
    });

    return response.data.items.sort((one, two) => {
        if (one.group instanceof Date && two.group instanceof Date) {
            return compareAsc(one.group, two.group);
        }

        return 0;
    });
};

/**
 * Get summary statistics across interaction metrics.
 * @param parameters Route filters.
 * @returns          React Query result.
 */
export const useInteractionMetricsSummaries = <T = IInteractionMetricsSummary<any>[]>(
    parameters: IInteractionsGetQueryParameters,
    configuration: Omit<UseQueryOptions<IInteractionMetricsSummary<any>[], AxiosError, T>, "queryKey" | "queryFn"> = {}
) => {
    // Grouping queries require the user's time zone. If the request is just for a single data point the time zone is
    // not strictly needed, but since the time zone is almost certainly available already in the query cache requesting
    // it here is not a big deal.
    const { data: timeZone, isLoading: isTimeZoneLoading } = useTimeZone();
    const hasTimeZone = typeof timeZone === "string";

    // Don't do the next request until the time zone is available and/or the options passed says the query is enabled.
    const queryConfiguration = { ...configuration, enabled: hasTimeZone && (configuration.enabled ?? true) };

    // Request data.
    const url = Route.InteractionMetricsSummaries;
    const queryParameters = { ...parameters, timeZone };

    const query = useQuery<IInteractionMetricsSummary<any>[], AxiosError, T>(
        [url, queryParameters],
        () => getInteractionMetricsSummaries(url, queryParameters),
        queryConfiguration
    );

    // If the either of the two queries are loading, the main one should be loading too. Otherwise components calling
    // this hook will all have to check both "isIdle" and "isLoading", and that's not clear really.
    const isLoading = query.isLoading || isTimeZoneLoading;
    return { ...query, isLoading };
};

/**
 * Get summary statistics across interaction metrics but only return the first result (useful when not grouping by
 * date).
 * @param parameters    Route filters.
 * @param configuration Query options.
 * @returns             React Query result.
 */
export const useInteractionMetricsSummary = (
    parameters: IInteractionsGetQueryParameters,
    configuration: Omit<
        UseQueryOptions<IInteractionMetricsSummary<any>[], AxiosError, IInteractionMetricsSummary<any>>,
        "queryKey" | "queryFn"
    > = {}
) => {
    return useInteractionMetricsSummaries(parameters, {
        ...configuration,
        select: (data: IInteractionMetricsSummary<any>[]) => data[0],
    });
};
